import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Heart, Sparkles, Sun, Zap, Target } from 'lucide-react';

const FeatureCard = ({ icon: Icon, title, description, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="group"
    >
      <div className="luxury-card-gold rounded-2xl p-8 h-full hover:border-accent/50 transition-all duration-500 hover:-translate-y-2">
        <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-colors">
          <Icon className="w-7 h-7 text-accent" />
        </div>
        <h3 className="font-display text-xl text-foreground mb-3">{title}</h3>
        <p className="text-muted-foreground font-body leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
};

export const PortalSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const features = [
    {
      icon: Heart,
      title: "SOIN",
      description: "Te libérer de tes blessures de vie, karmiques et transgénérationnelles."
    },
    {
      icon: Sparkles,
      title: "RÉVÉLATION",
      description: "Révéler les trésors cachés de ta personnalité (ce pour quoi tu t'es incarné·e)"
    },
    {
      icon: Sun,
      title: "RAYONNEMENT",
      description: "Oser être visible et prendre ta place (sans peur du jugement)"
    },
    {
      icon: Zap,
      title: "POTENTIELS",
      description: "Réveiller les pouvoirs énergétiques et psychiques qui dorment en toi"
    },
    {
      icon: Target,
      title: "MISSION",
      description: "Devenir praticien en soins quantiques"
    }
  ];

  return (
    <section ref={sectionRef} className="section-padding bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-radial-gold opacity-30 pointer-events-none" />
      
      <div className="container-luxury relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto mb-16"
        >
          <span className="text-accent font-body text-sm tracking-[0.3em] uppercase mb-4 block">
            Un portail de transformation
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight mb-6">
            Un portail pour changer de timeline
            <span className="text-gradient-gold block mt-2">et te déployer</span>
          </h2>
          <div className="divider-gold w-32 mx-auto my-8" />
          <p className="text-lg md:text-xl text-muted-foreground font-body leading-relaxed">
            Un voyage d'expansion de conscience pour retrouver une vie remplie 
            d'amour, de joie et de légèreté !
          </p>
        </motion.div>

        {/* Subtitle */}
        <motion.h3
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.3 }}
          className="text-center text-xl md:text-2xl font-display text-foreground mb-12"
        >
          Active la sagesse des Maîtres Dauphins pour :
        </motion.h3>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              {...feature}
              delay={0.1 + index * 0.1}
            />
          ))}
        </div>

        {/* Bottom Content */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="mt-20 grid lg:grid-cols-2 gap-12 items-center"
        >
          {/* Image */}
          <div className="relative order-2 lg:order-1">
            <motion.div
              className="absolute inset-0 bg-accent/10 blur-3xl rounded-full"
              animate={{ scale: [1, 1.05, 1], opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 6, repeat: Infinity }}
            />
            <img
              src="https://gillesdelieuze.com/wp-content/uploads/2025/03/gilles1385_dolphins_joy_happiness_neon_stellar_furist_ultra_det_d108e6d0-77b9-454e-9100-27fcbd17df2c-650x650.png"
              alt="Dauphins célestes"
              className="relative rounded-3xl shadow-lg border border-accent/20 w-full max-w-md mx-auto"
            />
          </div>

          {/* Text Content */}
          <div className="order-1 lg:order-2 space-y-6">
            <h3 className="font-display text-2xl md:text-3xl text-foreground leading-tight">
              L'École des Maîtres Dauphins est un programme réservé à celles et ceux qui souhaitent
              <span className="text-gradient-gold"> vivre une vie pleinement incarnée </span>
              dans :
            </h3>

            <div className="space-y-6">
              {[
                {
                  title: "La Joie",
                  desc: "la vraie joie d'être au-delà des circonstances et des désirs, renouer avec le bonheur d'être à chaque instant."
                },
                {
                  title: "L'Harmonie",
                  desc: "Vibrer à l'unisson avec tout ce qui est, collaborer et cocréer avec la Vie et se laisser traverser par le flow"
                },
                {
                  title: "Au Potentiel Spirituel plus Élevé",
                  desc: "Développer ses potentiels, sa conscience, libérer son rayonnement et vibrer sa plus belle note sur cette Terre."
                }
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.8 + index * 0.15 }}
                  className="glass-card rounded-xl p-5"
                >
                  <h4 className="font-display text-lg text-accent mb-2">{item.title}</h4>
                  <p className="text-muted-foreground font-body text-sm leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PortalSection;
